import { useEffect, useState } from "react";
import type { PreparedWorkflow } from "../../../../api/content/stepPlans";
import type { ThreadBindings } from "../../../../api/threads/liveMessages";
import { prepareStart } from "../../../../api/threads/threadsApi";
import type { UiError } from "../../../../errors/uiError";
import { loadAfterTypingPause } from "../../../../shared/loading/afterTypingPause";

export type PreparedStartState =
  | { readonly status: "idle" }
  | { readonly status: "loading"; readonly previous: PreparedWorkflow | null }
  | { readonly status: "loaded"; readonly value: PreparedWorkflow }
  | { readonly status: "failed"; readonly error: UiError };

interface PreparedStartInput {
  readonly projectId: string;
  /** `null` while no Workflow is picked: nothing is prepared. */
  readonly workflow: string | null;
  readonly bindings: ThreadBindings | null;
  readonly values: Readonly<Record<string, unknown>>;
}

type Answer = { readonly status: "loaded"; readonly value: PreparedWorkflow } | { readonly status: "failed"; readonly error: UiError };

/**
 * The server's preparation of this start (ADR-0032 D7; Johannes, 27 Oct, point 8): each step's
 * plan with the values given so far, read again once typing settles. The last answer stays in
 * view while the next is read.
 */
export function usePreparedStart({ projectId, workflow, bindings, values }: PreparedStartInput): PreparedStartState {
  const request = workflow === null || bindings === null ? null : JSON.stringify([projectId, workflow, bindings, values]);
  const [answer, setAnswer] = useState<{ readonly request: string; readonly answer: Answer } | null>(null);
  const [lastLoaded, setLastLoaded] = useState<PreparedWorkflow | null>(null);
  useEffect(() => {
    if (request === null || workflow === null || bindings === null) return undefined;
    return loadAfterTypingPause(
      (signal) => prepareStart({ projectId, workflow, bindings, inputValues: values }, signal),
      (next) => {
        setAnswer({ request, answer: next });
        if (next.status === "loaded") setLastLoaded(next.value);
      },
    );
  }, [request, projectId, workflow, bindings, values]);
  if (request === null) return { status: "idle" };
  return answer?.request === request ? answer.answer : { status: "loading", previous: lastLoaded?.reference === workflow ? lastLoaded : null };
}
